import React from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';

/**
 * /demos — an index of the standalone demos and POCs that are not part of the
 * workshop flow. Unlisted: reachable by URL, absent from Screen Manager and
 * facilitator navigation. Add a row here when a POC becomes showable.
 */
const DEMOS = [
  {
    path: '/statmap',
    title: 'Tilastokeskus map',
    summary: 'Finland choropleth painted from live Statistics Finland PxAPI data. Municipality, region and wellbeing-county layers, with a chat that picks the view.',
    source: 'Tilastokeskus PxAPI Beta + WFS boundaries, CC BY 4.0',
    date: '2026-09-14',
  },
  {
    path: '/labs/education-needs',
    title: 'Education needs 2040',
    summary: 'OPH workforce education-needs forecast: openings per year against completed degrees, BAU versus growth scenario, by education field and level.',
    source: 'OPH Koulutustarvetaulukko.xlsx (the Vipunen Power BI report has no clean public extract)',
    date: '2026-09-17',
  },
  {
    path: '/labs/vipupisteet-tki',
    title: 'Yhteiset vipupisteet (pivot points v3)',
    summary: 'Common themes across nine R&D collaboration experiments, lifted from their own reporting: theme cards with level and evidence, the compass of who has the problem and who the answer, and the six review lenses of the TKI brief.',
    source: 'Snapshot 19.10.2026 of the local TKI workshop map (anonymised project reports, pivot_points.v3 runs); read-only',
    date: '2026-10-19',
  },
  // The negotiation lab's four stages are tabs of one screen over one set of AI
  // personas, created on whichever stage is opened first and kept for the browser tab.
  {
    path: '/labs/metsaneuvottelu?stage=mixer',
    title: 'Metsämikseri (negotiation lab 1/4)',
    summary: 'Forestry stakeholders with conflicting goals as AI personas, each with an ideal and a red line per axis. Sliders split the wood between pulp, sawn goods and new bio-products and set protection; every persona\'s gauge reacts, and the fairest compromise (fewest red lines crossed) sits beside the largest total.',
    source: 'Luke and Statistics Finland figures (2023–2025) with the model\'s stated assumptions; personas generated live (Sonnet)',
    date: '2026-10-29',
  },
  {
    path: '/labs/metsaneuvottelu?stage=redLines',
    title: 'Punaiset viivat (negotiation lab 2/4)',
    summary: 'Each persona\'s span from red line to ideal on every axis, and the common ground where it exists. The AI proposes package deals across axes, each rechecked against every red line and playable in the mixer.',
    source: 'Personas and package deals generated live (Sonnet); rechecked with the mixer\'s model',
    date: '2026-10-29',
  },
  {
    path: '/labs/metsaneuvottelu?stage=veil',
    title: 'Tietämättömyyden verho (negotiation lab 3/4)',
    summary: 'Every persona ranks seven forestry solutions twice: as itself, then not knowing which stakeholder or generation it will be. A slope chart of the rankings, and how much the stakeholders agree before and after.',
    source: 'Rankings generated live (Sonnet); Borda count and Spearman agreement computed on screen',
    date: '2026-10-29',
  },
  {
    path: '/labs/metsaneuvottelu?stage=hybrids',
    title: 'Hybridit & Suomen tase (negotiation lab 4/4)',
    summary: 'Two solutions that pull against each other, two or three hybrids, and every persona\'s support: the least opposed option beside the most popular. Then each option scored for Finland and the world, carbon leakage included.',
    source: 'Hybrids and scores generated live (Sonnet); Finland\'s balance is unsourced, as the screen says',
    date: '2026-10-29',
  },
];

const Page = styled.div`
  min-height: 100vh;
  padding: 64px 24px;
  background: #0f1117;
  color: #e8eaf0;
  font-family: system-ui, -apple-system, sans-serif;
`;

const Inner = styled.div`
  max-width: 760px;
  margin: 0 auto;
`;

const Title = styled.h1`
  margin: 0 0 8px;
  font-size: 2rem;
  font-weight: 600;
`;

const Intro = styled.p`
  margin: 0 0 40px;
  color: #98a0b3;
  line-height: 1.5;
`;

const Card = styled(Link)`
  display: block;
  padding: 20px 22px;
  margin-bottom: 14px;
  border: 1px solid #262b38;
  border-radius: 10px;
  background: #161a23;
  text-decoration: none;
  color: inherit;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: #3d63dd;
    background: #1b2030;
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
`;

const CardTitle = styled.h2`
  margin: 0;
  font-size: 1.15rem;
  font-weight: 600;
`;

const CardDate = styled.span`
  flex-shrink: 0;
  font-size: 0.8rem;
  color: #6b7488;
  font-variant-numeric: tabular-nums;
`;

const CardSummary = styled.p`
  margin: 0 0 10px;
  color: #b6bdcd;
  line-height: 1.5;
`;

const CardSource = styled.p`
  margin: 0;
  font-size: 0.82rem;
  color: #6b7488;
`;

const Path = styled.code`
  font-size: 0.82rem;
  color: #7f92c9;
`;

export default function DemosScreen() {
  return (
    <Page>
      <Inner>
        <Title>Demos</Title>
        <Intro>
          Standalone demos and proofs of concept. These sit outside the workshop flow
          and are not wired into Screen Manager or facilitator navigation.
        </Intro>

        {DEMOS.map((demo) => (
          <Card key={demo.path} to={demo.path}>
            <CardTop>
              <CardTitle>{demo.title}</CardTitle>
              <CardDate>{demo.date}</CardDate>
            </CardTop>
            <CardSummary>{demo.summary}</CardSummary>
            <CardSource>{demo.source}</CardSource>
            <Path>{demo.path}</Path>
          </Card>
        ))}
      </Inner>
    </Page>
  );
}
